import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function ServiceManagementScreen({ navigation }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('services');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Service categories
  const [serviceCategories] = useState([
    'all', 'consulting', 'implementation', 'support', 'training', 'maintenance'
  ]);

  // Service statuses
  const [serviceStatuses] = useState([
    'all', 'active', 'inactive', 'development', 'discontinued'
  ]);

  // Services data
  const [services, setServices] = useState([
    {
      id: '1',
      name: 'Business Process Consulting',
      description: 'Strategic business process analysis and optimization',
      category: 'consulting',
      status: 'active',
      price: 2500.00,
      cost: 1200.00,
      margin: 52.00,
      currency: 'GBP',
      sku: 'SRV-BPC-001',
      duration: '3 months',
      deliverables: 5,
      clientsSold: 12,
      revenue: 30000.00,
      lastSale: '2024-01-16T13:20:00Z',
      createdDate: '2023-05-10T08:00:00Z',
      updatedDate: '2024-01-12T14:45:00Z',
      tags: ['consulting', 'business', 'strategy', 'optimization'],
      features: [
        'Process Analysis',
        'Workflow Optimization',
        'Performance Metrics',
        'Change Management',
        'Implementation Support',
        'Training & Documentation'
      ],
      specifications: {
        'Duration': '3 months',
        'Team Size': '3 consultants',
        'Deliverables': '5 reports',
        'Meetings': 'Weekly',
        'Support': 'Email & Phone',
        'Follow-up': '6 months'
      },
      performance: {
        salesTrend: 'up',
        salesChange: 15.8,
        profitMargin: 52.00,
        customerSatisfaction: 4.8,
        completionRate: 95.5,
        renewalRate: 85.0
      },
      milestones: [
        { name: 'Initial Assessment', duration: '2 weeks', completed: true },
        { name: 'Process Mapping', duration: '3 weeks', completed: true },
        { name: 'Analysis & Recommendations', duration: '4 weeks', completed: false },
        { name: 'Implementation Planning', duration: '2 weeks', completed: false },
        { name: 'Final Report & Handover', duration: '1 week', completed: false }
      ]
    },
    {
      id: '2',
      name: 'CRM System Implementation',
      description: 'Complete CRM system setup and configuration',
      category: 'implementation',
      status: 'active',
      price: 8500.00,
      cost: 4200.00,
      margin: 50.59,
      currency: 'GBP',
      sku: 'SRV-CRM-IMP',
      duration: '6 months',
      deliverables: 8,
      clientsSold: 8,
      revenue: 68000.00,
      lastSale: '2024-01-14T10:30:00Z',
      createdDate: '2023-07-15T12:00:00Z',
      updatedDate: '2024-01-10T16:20:00Z',
      tags: ['implementation', 'crm', 'software', 'integration'],
      features: [
        'System Configuration',
        'Data Migration',
        'Custom Workflows',
        'Integration Setup',
        'User Training',
        'Go-Live Support'
      ],
      specifications: {
        'Duration': '6 months',
        'Team Size': '4 specialists',
        'Data Migration': 'Included',
        'Training Hours': '40 hours',
        'Support': '3 months post-launch',
        'Warranty': '12 months'
      },
      performance: {
        salesTrend: 'up',
        salesChange: 22.3,
        profitMargin: 50.59,
        customerSatisfaction: 4.6,
        completionRate: 92.0,
        renewalRate: 75.0
      },
      milestones: [
        { name: 'Requirements Gathering', duration: '3 weeks', completed: true },
        { name: 'System Setup', duration: '4 weeks', completed: true },
        { name: 'Data Migration', duration: '6 weeks', completed: false },
        { name: 'Testing & QA', duration: '4 weeks', completed: false },
        { name: 'Training & Go-Live', duration: '3 weeks', completed: false }
      ]
    },
    {
      id: '3',
      name: '24/7 Technical Support',
      description: 'Round-the-clock technical support and maintenance',
      category: 'support',
      status: 'active',
      price: 299.99,
      cost: 120.00,
      margin: 59.99,
      currency: 'GBP',
      sku: 'SRV-SUP-247',
      duration: '12 months',
      deliverables: 0, // Ongoing service
      clientsSold: 45,
      revenue: 13499.55,
      lastSale: '2024-01-18T14:30:00Z',
      createdDate: '2023-03-20T09:00:00Z',
      updatedDate: '2024-01-15T11:10:00Z',
      tags: ['support', 'maintenance', '24/7', 'technical'],
      features: [
        '24/7 Phone Support',
        'Email Support',
        'Remote Assistance',
        'System Monitoring',
        'Preventive Maintenance',
        'Priority Response'
      ],
      specifications: {
        'Availability': '24/7/365',
        'Response Time': '< 2 hours',
        'Resolution Time': '< 24 hours',
        'Support Channels': 'Phone, Email, Chat',
        'Languages': 'English, Spanish',
        'Escalation': '3-tier support'
      },
      performance: {
        salesTrend: 'stable',
        salesChange: 2.1,
        profitMargin: 59.99,
        customerSatisfaction: 4.4,
        completionRate: 98.5,
        renewalRate: 92.0
      },
      milestones: [] // Ongoing service, no specific milestones
    },
    {
      id: '4',
      name: 'Safety Training Program',
      description: 'Comprehensive workplace safety training and certification',
      category: 'training',
      status: 'active',
      price: 1299.99,
      cost: 520.00,
      margin: 59.99,
      currency: 'GBP',
      sku: 'SRV-TRN-SAF',
      duration: '2 months',
      deliverables: 3,
      clientsSold: 18,
      revenue: 23399.82,
      lastSale: '2024-01-17T11:45:00Z',
      createdDate: '2023-04-05T14:30:00Z',
      updatedDate: '2024-01-08T09:15:00Z',
      tags: ['training', 'safety', 'certification', 'workplace'],
      features: [
        'Interactive Training Modules',
        'Hands-on Workshops',
        'Safety Assessments',
        'Certification Exams',
        'Progress Tracking',
        'Compliance Reporting'
      ],
      specifications: {
        'Duration': '2 months',
        'Modules': '12 training modules',
        'Workshop Hours': '16 hours',
        'Certification': 'Valid 2 years',
        'Languages': '5 languages',
        'Materials': 'Included'
      },
      performance: {
        salesTrend: 'up',
        salesChange: 18.7,
        profitMargin: 59.99,
        customerSatisfaction: 4.7,
        completionRate: 89.5,
        renewalRate: 78.0
      },
      milestones: [
        { name: 'Training Needs Assessment', duration: '1 week', completed: true },
        { name: 'Module Delivery', duration: '6 weeks', completed: false },
        { name: 'Practical Workshops', duration: '2 weeks', completed: false },
        { name: 'Certification & Evaluation', duration: '1 week', completed: false }
      ]
    },
    {
      id: '5',
      name: 'System Maintenance Package',
      description: 'Preventive maintenance and system optimization',
      category: 'maintenance',
      status: 'active',
      price: 599.99,
      cost: 240.00,
      margin: 59.99,
      currency: 'GBP',
      sku: 'SRV-MNT-SYS',
      duration: '12 months',
      deliverables: 4,
      clientsSold: 25,
      revenue: 14999.75,
      lastSale: '2024-01-15T16:20:00Z',
      createdDate: '2023-06-01T10:00:00Z',
      updatedDate: '2024-01-12T13:45:00Z',
      tags: ['maintenance', 'optimization', 'preventive', 'system'],
      features: [
        'Regular System Checkups',
        'Performance Optimization',
        'Security Updates',
        'Backup Management',
        'Health Reports',
        'Emergency Support'
      ],
      specifications: {
        'Frequency': 'Quarterly',
        'Checkup Duration': '4 hours',
        'Reports': 'Quarterly',
        'Emergency Support': 'Included',
        'Response Time': '< 4 hours',
        'Coverage': 'Business hours'
      },
      performance: {
        salesTrend: 'up',
        salesChange: 12.5,
        profitMargin: 59.99,
        customerSatisfaction: 4.5,
        completionRate: 96.0,
        renewalRate: 88.0
      },
      milestones: [
        { name: 'Q1 System Checkup', duration: '1 day', completed: true },
        { name: 'Q2 System Checkup', duration: '1 day', completed: false },
        { name: 'Q3 System Checkup', duration: '1 day', completed: false },
        { name: 'Q4 System Checkup', duration: '1 day', completed: false }
      ]
    },
    {
      id: '6',
      name: 'Legacy System Migration',
      description: 'Complete legacy system modernization and migration',
      category: 'implementation',
      status: 'development',
      price: 25000.00,
      cost: 14000.00,
      margin: 44.00,
      currency: 'GBP',
      sku: 'SRV-LEG-MIG',
      duration: '12 months',
      deliverables: 10,
      clientsSold: 2,
      revenue: 50000.00,
      lastSale: '2024-01-10T09:30:00Z',
      createdDate: '2023-11-01T15:00:00Z',
      updatedDate: '2024-01-15T11:20:00Z',
      tags: ['migration', 'legacy', 'modernization', 'implementation'],
      features: [
        'Legacy System Analysis',
        'Migration Strategy',
        'Data Conversion',
        'System Integration',
        'Testing & Validation',
        'Go-Live Support'
      ],
      specifications: {
        'Duration': '12 months',
        'Team Size': '6-8 specialists',
        'Technologies': 'Modern stack',
        'Data Migration': 'Complete',
        'Testing': 'Comprehensive',
        'Training': 'Included'
      },
      performance: {
        salesTrend: 'up',
        salesChange: 100.0,
        profitMargin: 44.00,
        customerSatisfaction: 4.9,
        completionRate: 85.0,
        renewalRate: 60.0
      },
      milestones: [
        { name: 'System Analysis', duration: '4 weeks', completed: true },
        { name: 'Migration Planning', duration: '6 weeks', completed: false },
        { name: 'Development & Setup', duration: '20 weeks', completed: false },
        { name: 'Data Migration', duration: '8 weeks', completed: false },
        { name: 'Testing & Go-Live', duration: '10 weeks', completed: false }
      ]
    }
  ]);

  // Service analytics
  const [serviceAnalytics, setServiceAnalytics] = useState({
    totalServices: 6,
    activeServices: 5,
    totalRevenue: 199899.12,
    totalClients: 110,
    avgMargin: 54.26,
    topPerformer: 'CRM System Implementation',
    completionRate: 92.8,
    renewalRate: 79.7
  });

  // Service categories performance
  const [categoryPerformance, setCategoryPerformance] = useState([
    {
      id: '1',
      name: 'Consulting',
      services: 1,
      revenue: 30000.00,
      clients: 12,
      avgMargin: 52.00,
      trend: 'up',
      change: 15.8,
      color: '#3B82F6'
    },
    {
      id: '2',
      name: 'Implementation',
      services: 2,
      revenue: 118000.00,
      clients: 10,
      avgMargin: 47.30,
      trend: 'up',
      change: 61.15,
      color: '#10B981'
    },
    {
      id: '3',
      name: 'Support',
      services: 1,
      revenue: 13499.55,
      clients: 45,
      avgMargin: 59.99,
      trend: 'stable',
      change: 2.1,
      color: '#F59E0B'
    },
    {
      id: '4',
      name: 'Training',
      services: 1,
      revenue: 23399.82,
      clients: 18,
      avgMargin: 59.99,
      trend: 'up',
      change: 18.7,
      color: '#8B5CF6'
    },
    {
      id: '5',
      name: 'Maintenance',
      services: 1,
      revenue: 14999.75,
      clients: 25,
      avgMargin: 59.99,
      trend: 'up',
      change: 12.5,
      color: '#EF4444'
    }
  ]);

  // Service insights
  const [serviceInsights, setServiceInsights] = useState([
    {
      id: '1',
      type: 'opportunity',
      title: 'High-Value Implementation Services',
      description: 'Implementation services generate 59% of total revenue with highest client value',
      impact: 'high',
      action: 'Expand implementation service portfolio and team capacity',
      icon: 'trending-up',
      color: '#10B981'
    },
    {
      id: '2',
      type: 'achievement',
      title: 'Excellent Service Completion Rate',
      description: 'Overall service completion rate of 92.8% exceeds industry standards',
      impact: 'high',
      action: 'Use completion rate as competitive advantage in proposals',
      icon: 'trophy',
      color: '#3B82F6'
    },
    {
      id: '3',
      type: 'concern',
      title: 'Training Service Completion Rate',
      description: 'Training services show lower completion rate at 89.5%',
      impact: 'medium',
      action: 'Review training delivery methods and client engagement strategies',
      icon: 'alert-circle',
      color: '#F59E0B'
    },
    {
      id: '4',
      type: 'recommendation',
      title: 'Leverage High Renewal Rates',
      description: 'Support services show 92% renewal rate, highest among all categories',
      impact: 'medium',
      action: 'Develop upselling strategies for support service clients',
      icon: 'lightbulb',
      color: '#8B5CF6'
    }
  ]);

  // Active service projects
  const [activeProjects, setActiveProjects] = useState([
    {
      id: '1',
      serviceId: '1',
      serviceName: 'Business Process Consulting',
      client: 'TechCorp Ltd',
      startDate: '2024-01-01T09:00:00Z',
      endDate: '2024-04-01T17:00:00Z',
      progress: 65,
      status: 'in-progress',
      currentMilestone: 'Analysis & Recommendations',
      nextMilestone: 'Implementation Planning',
      teamMembers: ['Sarah Johnson', 'Mike Chen', 'David Brown'],
      budget: 2500.00,
      spent: 1625.00,
      remaining: 875.00
    },
    {
      id: '2',
      serviceId: '2',
      serviceName: 'CRM System Implementation',
      client: 'Green Builders Ltd',
      startDate: '2023-12-01T09:00:00Z',
      endDate: '2024-06-01T17:00:00Z',
      progress: 45,
      status: 'in-progress',
      currentMilestone: 'Data Migration',
      nextMilestone: 'Testing & QA',
      teamMembers: ['Emma Wilson', 'Alex Turner', 'Lisa Chen', 'Tom Wilson'],
      budget: 8500.00,
      spent: 3825.00,
      remaining: 4675.00
    },
    {
      id: '3',
      serviceId: '4',
      serviceName: 'Safety Training Program',
      client: 'BuildFast Construction',
      startDate: '2024-01-15T09:00:00Z',
      endDate: '2024-03-15T17:00:00Z',
      progress: 25,
      status: 'in-progress',
      currentMilestone: 'Module Delivery',
      nextMilestone: 'Practical Workshops',
      teamMembers: ['Rachel Green', 'Mark Davis'],
      budget: 1299.99,
      spent: 324.99,
      remaining: 975.00
    },
    {
      id: '4',
      serviceId: '6',
      serviceName: 'Legacy System Migration',
      client: 'Urban Development Co',
      startDate: '2023-11-15T09:00:00Z',
      endDate: '2024-11-15T17:00:00Z',
      progress: 15,
      status: 'in-progress',
      currentMilestone: 'Migration Planning',
      nextMilestone: 'Development & Setup',
      teamMembers: ['John Smith', 'Anna Lee', 'Chris Brown', 'Maya Patel', 'Sam Wilson', 'Kate Johnson'],
      budget: 25000.00,
      spent: 3750.00,
      remaining: 21250.00
    }
  ]);

  useEffect(() => {
    initializeScreen();
  }, []);

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      // Simulate loading data
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load service data');
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-GB').format(number);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': '#10B981',
      'inactive': '#6B7280',
      'development': '#F59E0B',
      'discontinued': '#EF4444',
      'in-progress': '#3B82F6',
      'completed': '#10B981',
      'on-hold': '#F59E0B',
      'cancelled': '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getTrendIcon = (trend) => {
    const icons = {
      'up': 'trending-up',
      'down': 'trending-down',
      'stable': 'trending-neutral'
    };
    return icons[trend] || 'minus';
  };

  const getTrendColor = (trend) => {
    const colors = {
      'up': '#10B981',
      'down': '#EF4444',
      'stable': '#6B7280'
    };
    return colors[trend] || '#6B7280';
  };

  const getImpactColor = (impact) => {
    const colors = {
      'high': '#EF4444',
      'medium': '#F59E0B',
      'low': '#10B981'
    };
    return colors[impact] || '#6B7280';
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = String(service.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(service.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(service.sku ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || service.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || service.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const addService = () => {
    Alert.alert(
      'Add Service',
      'Service creation functionality would be implemented here.',
      [{ text: 'OK' }]
    );
  };

  const editService = (serviceId) => {
    Alert.alert(
      'Edit Service',
      `Edit functionality for service ${serviceId} would be implemented here.`,
      [{ text: 'OK' }]
    );
  };

  const deleteService = (serviceId) => {
    Alert.alert(
      'Delete Service',
      'Are you sure you want to delete this service?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            setServices(services.filter(s => s.id !== serviceId));
            Alert.alert('Success', 'Service deleted successfully');
          }
        }
      ]
    );
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#1F2937', '#374151']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          onPress={() => navigation?.goBack()}
          style={styles.backButton}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Service Management</Text>
          <Text style={styles.headerSubtitle}>Manage Services & Projects</Text>
        </View>
        
        <TouchableOpacity 
          style={styles.headerButton}
          onPress={addService}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      >
        {[
          { key: 'services', title: 'Services', icon: 'cog' },
          { key: 'projects', title: 'Active Projects', icon: 'briefcase' },
          { key: 'categories', title: 'Categories', icon: 'folder-multiple' },
          { key: 'analytics', title: 'Analytics', icon: 'chart-line' },
          { key: 'insights', title: 'Insights', icon: 'lightbulb' }
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.key ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSearchAndFilter = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchInputContainer}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search services..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        )}
      </View>
      
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContainer}
      >
        <Text style={styles.filterLabel}>Category:</Text>
        {serviceCategories.map((category) => (
          <TouchableOpacity
            key={category}
            style={[
              styles.filterButton,
              filterCategory === category && styles.activeFilterButton
            ]}
            onPress={() => setFilterCategory(category)}
          >
            <Text style={[
              styles.filterButtonText,
              filterCategory === category && styles.activeFilterButtonText
            ]}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContainer}
      >
        <Text style={styles.filterLabel}>Status:</Text>
        {serviceStatuses.map((status) => (
          <TouchableOpacity
            key={status}
            style={[
              styles.filterButton,
              filterStatus === status && styles.activeFilterButton
            ]}
            onPress={() => setFilterStatus(status)}
          >
            <Text style={[
              styles.filterButtonText,
              filterStatus === status && styles.activeFilterButtonText
            ]}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderServiceItem = ({ item }) => (
    <View style={styles.serviceCard}>
      <View style={styles.serviceHeader}>
        <View style={styles.serviceInfo}>
          <Text style={styles.serviceName}>{item.name}</Text>
          <Text style={styles.serviceDescription}>{item.description}</Text>
          <Text style={styles.serviceSku}>SKU: {item.sku}</Text>
        </View>
        <View style={styles.serviceBadges}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusBadgeText}>{item.status}</Text>
          </View>
        </View>
      </View>
      
      <View style={styles.serviceMetrics}>
        <View style={styles.serviceMetricItem}>
          <MaterialCommunityIcons name="currency-usd" size={16} color="#10B981" />
          <Text style={styles.serviceMetricLabel}>Price</Text>
          <Text style={styles.serviceMetricValue}>{formatCurrency(item.price)}</Text>
        </View>
        <View style={styles.serviceMetricItem}>
          <MaterialCommunityIcons name="chart-line" size={16} color="#3B82F6" />
          <Text style={styles.serviceMetricLabel}>Margin</Text>
          <Text style={styles.serviceMetricValue}>{formatPercentage(item.margin)}</Text>
        </View>
        <View style={styles.serviceMetricItem}>
          <MaterialCommunityIcons name="account-group" size={16} color="#F59E0B" />
          <Text style={styles.serviceMetricLabel}>Clients</Text>
          <Text style={styles.serviceMetricValue}>{item.clientsSold}</Text>
        </View>
        <View style={styles.serviceMetricItem}>
          <MaterialCommunityIcons name="clock" size={16} color="#8B5CF6" />
          <Text style={styles.serviceMetricLabel}>Duration</Text>
          <Text style={styles.serviceMetricValue}>{item.duration}</Text>
        </View>
      </View>
      
      <View style={styles.servicePerformance}>
        <View style={styles.servicePerformanceItem}>
          <MaterialCommunityIcons 
            name={getTrendIcon(item.performance.salesTrend)} 
            size={16} 
            color={getTrendColor(item.performance.salesTrend)} 
          />
          <Text style={[styles.servicePerformanceText, { color: getTrendColor(item.performance.salesTrend) }]}>
            {item.performance.salesChange > 0 ? '+' : ''}{item.performance.salesChange}% sales
          </Text>
        </View>
        <View style={styles.servicePerformanceItem}>
          <MaterialCommunityIcons name="star" size={16} color="#EF4444" />
          <Text style={styles.servicePerformanceText}>
            {item.performance.customerSatisfaction}/5 rating
          </Text>
        </View>
        <View style={styles.servicePerformanceItem}>
          <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
          <Text style={styles.servicePerformanceText}>
            {formatPercentage(item.performance.completionRate)} completion
          </Text>
        </View>
      </View>
      
      <View style={styles.serviceTags}>
        {item.tags.slice(0, 3).map((tag, index) => (
          <View key={index} style={styles.serviceTag}>
            <Text style={styles.serviceTagText}>{tag}</Text>
          </View>
        ))}
        {item.tags.length > 3 && (
          <View style={styles.serviceTag}>
            <Text style={styles.serviceTagText}>+{item.tags.length - 3}</Text>
          </View>
        )}
      </View>
      
      <View style={styles.serviceActions}>
        <TouchableOpacity 
          style={styles.serviceActionButton}
          onPress={() => {
            setSelectedService(item);
            setShowServiceModal(true);
          }}
        >
          <MaterialCommunityIcons name="eye" size={16} color="#3B82F6" />
          <Text style={styles.serviceActionButtonText}>View</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.serviceActionButton}
          onPress={() => editService(item.id)}
        >
          <MaterialCommunityIcons name="pencil" size={16} color="#10B981" />
          <Text style={styles.serviceActionButtonText}>Edit</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.serviceActionButton}
          onPress={() => deleteService(item.id)}
        >
          <MaterialCommunityIcons name="delete" size={16} color="#EF4444" />
          <Text style={styles.serviceActionButtonText}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderProjectItem = ({ item }) => (
    <View style={styles.projectCard}>
      <View style={styles.projectHeader}>
        <View style={styles.projectInfo}>
          <Text style={styles.projectService}>{item.serviceName}</Text>
          <Text style={styles.projectClient}>{item.client}</Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
          <Text style={styles.statusBadgeText}>{item.status}</Text>
        </View>
      </View>
      
      <View style={styles.projectProgress}>
        <View style={styles.projectProgressHeader}>
          <Text style={styles.projectProgressLabel}>Progress</Text>
          <Text style={styles.projectProgressValue}>{item.progress}%</Text>
        </View>
        <View style={styles.progressBar}>
          <View 
            style={[
              styles.progressFill, 
              { 
                width: `${item.progress}%`,
                backgroundColor: '#3B82F6'
              }
            ]} 
          />
        </View>
      </View>
      
      <View style={styles.projectMilestones}>
        <Text style={styles.projectMilestonesTitle}>Current Milestone</Text>
        <Text style={styles.projectCurrentMilestone}>{item.currentMilestone}</Text>
        <Text style={styles.projectNextMilestone}>Next: {item.nextMilestone}</Text>
      </View>
      
      <View style={styles.projectBudget}>
        <View style={styles.projectBudgetItem}>
          <Text style={styles.projectBudgetLabel}>Budget</Text>
          <Text style={styles.projectBudgetValue}>{formatCurrency(item.budget)}</Text>
        </View>
        <View style={styles.projectBudgetItem}>
          <Text style={styles.projectBudgetLabel}>Spent</Text>
          <Text style={styles.projectBudgetValue}>{formatCurrency(item.spent)}</Text>
        </View>
        <View style={styles.projectBudgetItem}>
          <Text style={styles.projectBudgetLabel}>Remaining</Text>
          <Text style={[styles.projectBudgetValue, { color: '#10B981' }]}>
            {formatCurrency(item.remaining)}
          </Text>
        </View>
      </View>
      
      <View style={styles.projectTeam}>
        <Text style={styles.projectTeamTitle}>Team ({item.teamMembers.length})</Text>
        <View style={styles.projectTeamMembers}>
          {item.teamMembers.slice(0, 3).map((member, index) => (
            <View key={index} style={styles.projectTeamMember}>
              <Text style={styles.projectTeamMemberText}>
                {member.split(' ').map(n => n[0]).join('')}
              </Text>
            </View>
          ))}
          {item.teamMembers.length > 3 && (
            <View style={styles.projectTeamMember}>
              <Text style={styles.projectTeamMemberText}>+{item.teamMembers.length - 3}</Text>
            </View>
          )}
        </View>
      </View>
      
      <View style={styles.projectDates}>
        <Text style={styles.projectDate}>
          Started: {formatDate(item.startDate)}
        </Text>
        <Text style={styles.projectDate}>
          Due: {formatDate(item.endDate)}
        </Text>
      </View>
    </View>
  );

  const renderCategoryItem = ({ item }) => (
    <View style={styles.categoryCard}>
      <View style={styles.categoryHeader}>
        <View style={styles.categoryInfo}>
          <View style={[styles.categoryIcon, { backgroundColor: item.color }]}>
            <MaterialCommunityIcons name="cog" size={24} color="#FFFFFF" />
          </View>
          <View style={styles.categoryDetails}>
            <Text style={styles.categoryName}>{item.name}</Text>
            <Text style={styles.categoryServices}>{item.services} services</Text>
          </View>
        </View>
        <View style={styles.categoryTrend}>
          <MaterialCommunityIcons 
            name={getTrendIcon(item.trend)} 
            size={16} 
            color={getTrendColor(item.trend)} 
          />
          <Text style={[styles.categoryTrendText, { color: getTrendColor(item.trend) }]}>
            {item.change > 0 ? '+' : ''}{item.change}%
          </Text>
        </View>
      </View>
      
      <View style={styles.categoryMetrics}>
        <View style={styles.categoryMetricItem}>
          <Text style={styles.categoryMetricLabel}>Revenue</Text>
          <Text style={styles.categoryMetricValue}>{formatCurrency(item.revenue)}</Text>
        </View>
        <View style={styles.categoryMetricItem}>
          <Text style={styles.categoryMetricLabel}>Clients</Text>
          <Text style={styles.categoryMetricValue}>{item.clients}</Text>
        </View>
        <View style={styles.categoryMetricItem}>
          <Text style={styles.categoryMetricLabel}>Avg Margin</Text>
          <Text style={styles.categoryMetricValue}>{formatPercentage(item.avgMargin)}</Text>
        </View>
      </View>
    </View>
  );

  const renderAnalytics = () => (
    <ScrollView style={styles.analyticsContainer}>
      <View style={styles.analyticsOverview}>
        <Text style={styles.analyticsTitle}>Service Analytics</Text>
        
        <View style={styles.analyticsGrid}>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="cog" size={24} color="#3B82F6" />
            <Text style={styles.analyticsValue}>{serviceAnalytics.totalServices}</Text>
            <Text style={styles.analyticsLabel}>Total Services</Text>
          </View>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="check-circle" size={24} color="#10B981" />
            <Text style={styles.analyticsValue}>{serviceAnalytics.activeServices}</Text>
            <Text style={styles.analyticsLabel}>Active Services</Text>
          </View>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="currency-usd" size={24} color="#F59E0B" />
            <Text style={styles.analyticsValue}>{formatCurrency(serviceAnalytics.totalRevenue)}</Text>
            <Text style={styles.analyticsLabel}>Total Revenue</Text>
          </View>
          <View style={styles.analyticsItem}>
            <MaterialCommunityIcons name="account-group" size={24} color="#8B5CF6" />
            <Text style={styles.analyticsValue}>{serviceAnalytics.totalClients}</Text>
            <Text style={styles.analyticsLabel}>Total Clients</Text>
          </View>
        </View>
        
        <View style={styles.analyticsMetrics}>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Average Margin</Text>
            <Text style={styles.analyticsMetricValue}>{formatPercentage(serviceAnalytics.avgMargin)}</Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Top Performer</Text>
            <Text style={styles.analyticsMetricValue}>{serviceAnalytics.topPerformer}</Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Completion Rate</Text>
            <Text style={[styles.analyticsMetricValue, { color: '#10B981' }]}>
              {formatPercentage(serviceAnalytics.completionRate)}
            </Text>
          </View>
          <View style={styles.analyticsMetricItem}>
            <Text style={styles.analyticsMetricLabel}>Renewal Rate</Text>
            <Text style={styles.analyticsMetricValue}>{formatPercentage(serviceAnalytics.renewalRate)}</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderInsightItem = ({ item }) => (
    <View style={styles.insightCard}>
      <View style={styles.insightHeader}>
        <View style={styles.insightIcon}>
          <MaterialCommunityIcons 
            name={item.icon} 
            size={24} 
            color={item.color} 
          />
        </View>
        <View style={styles.insightInfo}>
          <Text style={styles.insightTitle}>{item.title}</Text>
          <View style={[styles.impactBadge, { backgroundColor: getImpactColor(item.impact) }]}>
            <Text style={styles.impactBadgeText}>{item.impact} impact</Text>
          </View>
        </View>
      </View>
      
      <Text style={styles.insightDescription}>{item.description}</Text>
      
      <View style={styles.insightAction}>
        <MaterialCommunityIcons name="lightbulb" size={16} color="#F59E0B" />
        <Text style={styles.insightActionText}>{item.action}</Text>
      </View>
    </View>
  );

  // Load services from API on mount
  useEffect(() => {
    const loadFromApi = async () => {
      try {
        const [servicesResp, analyticsResp] = await Promise.all([
          apiRequest('services'),
          apiRequest('service-analytics'),
        ]);
        if (servicesResp.success && servicesResp.data) setServices(servicesResp.data);
        if (analyticsResp.success && analyticsResp.data) {
          setServiceAnalytics(analyticsResp.data);
          if (analyticsResp.data.categoryPerformance) setCategoryPerformance(analyticsResp.data.categoryPerformance);
          if (analyticsResp.data.insights) setServiceInsights(analyticsResp.data.insights);
        }
      } catch (error) {
        console.warn('API unavailable, using local data:', error.message);
      }
    };
    loadFromApi();
  }, []);

  // Save service via API
  const saveServiceToApi = async (service) => {
    try {
      await apiRequest('services', {
        method: service.id ? 'PUT' : 'POST',
        body: service,
      });
    } catch (error) {
      console.warn('API save failed:', error.message);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'services':
        return (
          <View style={styles.servicesContainer}>
            {renderSearchAndFilter()}
            <FlatList
              data={filteredServices}
              keyExtractor={(item) => item.id}
              renderItem={renderServiceItem}
              contentContainerStyle={styles.listContainer}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={onRefresh}
                  colors={['#3B82F6']}
                />
              }
              showsVerticalScrollIndicator={false}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <MaterialCommunityIcons name="cog-off" size={64} color="#D1D5DB" />
                  <Text style={styles.emptyTitle}>No services found</Text>
                  <Text style={styles.emptySubtitle}>
                    <Text>{searchQuery ? 'Try adjusting your search criteria' : 'Add your first service to get started'}</Text>
                  </Text>
                </View>
              }
            />
          </View>
        );
      case 'projects':
        return (
          <FlatList
            data={activeProjects}
            keyExtractor={(item) => item.id}
            renderItem={renderProjectItem}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#3B82F6']}
              />
            }
            showsVerticalScrollIndicator={false}
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <MaterialCommunityIcons name="briefcase-off" size={64} color="#D1D5DB" />
                <Text style={styles.emptyTitle}>No active projects</Text>
                <Text style={styles.emptySubtitle}>All projects are completed or no projects started yet</Text>
              </View>
            }
          />
        );
      case 'categories':
        return (
          <FlatList
            data={categoryPerformance}
            keyExtractor={(item) => item.id}
            renderItem={renderCategoryItem}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#3B82F6']}
              />
            }
            showsVerticalScrollIndicator={false}
            numColumns={2}
            columnWrapperStyle={styles.row}
          />
        );
      case 'analytics':
        return renderAnalytics();
      case 'insights':
        return (
          <FlatList
            data={serviceInsights}
            keyExtractor={(item) => item.id}
            renderItem={renderInsightItem}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#3B82F6']}
              />
            }
            showsVerticalScrollIndicator={false}
          />
        );
      default:
        return null;
    }
  };

  const renderServiceModal = () => (
    <Modal
      visible={showServiceModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => {
        setShowServiceModal(false);
        setSelectedService(null);
      }}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            {selectedService ? selectedService.name : 'Service Details'}
          </Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => {
              setShowServiceModal(false);
              setSelectedService(null);
            }}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        {selectedService && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Service Information</Text>
              <Text style={styles.modalText}>
                Description: {selectedService.description}{'\n'}
                Category: {selectedService.category}{'\n'}
                Status: {selectedService.status}{'\n'}
                SKU: {selectedService.sku}{'\n'}
                Duration: {selectedService.duration}{'\n'}
                Deliverables: {selectedService.deliverables}{'\n'}
                Created: {formatDate(selectedService.createdDate)}{'\n'}
                Updated: {formatDate(selectedService.updatedDate)}
              </Text>
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Pricing & Financials</Text>
              <Text style={styles.modalText}>
                Price: {formatCurrency(selectedService.price)}{'\n'}
                Cost: {formatCurrency(selectedService.cost)}{'\n'}
                Margin: {formatPercentage(selectedService.margin)}{'\n'}
                Clients Served: {selectedService.clientsSold}{'\n'}
                Total Revenue: {formatCurrency(selectedService.revenue)}{'\n'}
                <Text>{selectedService.lastSale && `Last Sale: ${formatDate(selectedService.lastSale)}`}</Text>
              </Text>
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Performance</Text>
              <Text style={styles.modalText}>
                Sales Trend: {selectedService.performance.salesTrend} ({selectedService.performance.salesChange > 0 ? '+' : ''}{selectedService.performance.salesChange}%){'\n'}
                Customer Satisfaction: {selectedService.performance.customerSatisfaction}/5{'\n'}
                Completion Rate: {formatPercentage(selectedService.performance.completionRate)}{'\n'}
                Renewal Rate: {formatPercentage(selectedService.performance.renewalRate)}
              </Text>
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Features</Text>
              {selectedService.features.map((feature, index) => (
                <Text key={index} style={styles.modalText}>
                  • {feature}
                </Text>
              ))}
            </View>
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Specifications</Text>
              {Object.entries(selectedService.specifications).map(([key, value], index) => (
                <Text key={index} style={styles.modalText}>
                  {key}: {value}
                </Text>
              ))}
            </View>
            
            {selectedService.milestones.length > 0 && (
              <View style={styles.modalSection}>
                <Text style={styles.modalSectionTitle}>Service Milestones</Text>
                {selectedService.milestones.map((milestone, index) => (
                  <View key={index} style={styles.modalMilestone}>
                    <MaterialCommunityIcons 
                      name={milestone.completed ? 'check-circle' : 'circle-outline'} 
                      size={16} 
                      color={milestone.completed ? '#10B981' : '#6B7280'} 
                    />
                    <Text style={[styles.modalText, { marginLeft: 8, flex: 1 }]}>
                      {milestone.name} ({milestone.duration})
                    </Text>
                  </View>
                ))}
              </View>
            )}
            
            <View style={styles.modalSection}>
              <Text style={styles.modalSectionTitle}>Tags</Text>
              <View style={styles.modalTags}>
                {selectedService.tags.map((tag, index) => (
                  <View key={index} style={styles.modalTag}>
                    <Text style={styles.modalTagText}>{tag}</Text>
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>
        )}
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#3B82F6" />
        <Text style={styles.loadingText}>Loading Service Data...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1F2937" />
      
      {renderHeader()}
      {renderTabs()}
      {renderContent()}
      {renderServiceModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  // Header styles
  header: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Tabs styles
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  tabsContent: {
    paddingHorizontal: 16,
  },
  
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
  },
  
  activeTab: {
    backgroundColor: '#EBF4FF',
  },
  
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
    fontWeight: '500',
  },
  
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  // Search and filter styles
  searchContainer: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
  },
  
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1F2937',
    marginLeft: 12,
  },
  
  filterContainer: {
    paddingVertical: 4,
    alignItems: 'center',
  },
  
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginRight: 12,
  },
  
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  activeFilterButton: {
    backgroundColor: '#EBF4FF',
    borderColor: '#3B82F6',
  },
  
  filterButtonText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  activeFilterButtonText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  // List styles
  listContainer: {
    padding: 16,
  },
  
  row: {
    justifyContent: 'space-between',
  },
  
  servicesContainer: {
    flex: 1,
  },
  
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 64,
  },
  
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
  },
  
  emptySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  
  // Service card styles
  serviceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  serviceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  
  serviceInfo: {
    flex: 1,
  },
  
  serviceName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  serviceDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
    lineHeight: 20,
  },
  
  serviceSku: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 4,
  },
  
  serviceBadges: {
    alignItems: 'flex-end',
  },
  
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  statusBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  serviceMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  
  serviceMetricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  
  serviceMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 4,
    marginRight: 4,
  },
  
  serviceMetricValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  servicePerformance: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  
  servicePerformanceItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  servicePerformanceText: {
    fontSize: 12,
    marginLeft: 4,
  },
  
  serviceTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  
  serviceTag: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  
  serviceTagText: {
    fontSize: 10,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  serviceActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 12,
  },
  
  serviceActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    flex: 1,
    marginHorizontal: 4,
    justifyContent: 'center',
  },
  
  serviceActionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  
  // Project card styles
  projectCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  projectHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  
  projectInfo: {
    flex: 1,
  },
  
  projectService: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  projectClient: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  
  projectProgress: {
    marginBottom: 16,
  },
  
  projectProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  projectProgressLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  projectProgressValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
  },
  
  progressBar: {
    height: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
  },
  
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  
  projectMilestones: {
    marginBottom: 16,
  },
  
  projectMilestonesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  projectCurrentMilestone: {
    fontSize: 14,
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  projectNextMilestone: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  
  projectBudget: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  
  projectBudgetItem: {
    alignItems: 'center',
  },
  
  projectBudgetLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  projectBudgetValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  projectTeam: {
    marginBottom: 16,
  },
  
  projectTeamTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  projectTeamMembers: {
    flexDirection: 'row',
  },
  
  projectTeamMember: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  
  projectTeamMemberText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  projectDates: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 12,
  },
  
  projectDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  // Category card styles
  categoryCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  categoryHeader: {
    marginBottom: 16,
  },
  
  categoryInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  categoryIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  categoryDetails: {
    marginLeft: 8,
  },
  
  categoryName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  categoryServices: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  
  categoryTrend: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  categoryTrendText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  
  categoryMetrics: {
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 12,
  },
  
  categoryMetricItem: {
    marginBottom: 8,
  },
  
  categoryMetricLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  categoryMetricValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Analytics styles
  analyticsContainer: {
    padding: 16,
  },
  
  analyticsOverview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  analyticsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 20,
  },
  
  analyticsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  
  analyticsItem: {
    width: (width - 64) / 2,
    alignItems: 'center',
    marginBottom: 16,
  },
  
  analyticsValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 8,
    textAlign: 'center',
  },
  
  analyticsLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
    textAlign: 'center',
  },
  
  analyticsMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 16,
  },
  
  analyticsMetricItem: {
    width: (width - 64) / 2,
    marginBottom: 12,
  },
  
  analyticsMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  analyticsMetricValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Insight card styles
  insightCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  insightIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  insightInfo: {
    flex: 1,
    marginLeft: 12,
  },
  
  insightTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  impactBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  
  impactBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  insightDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 12,
  },
  
  insightAction: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFBEB',
    padding: 12,
    borderRadius: 8,
  },
  
  insightActionText: {
    flex: 1,
    fontSize: 14,
    color: '#92400E',
    marginLeft: 8,
    lineHeight: 20,
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalContent: {
    flex: 1,
    padding: 16,
  },
  
  modalSection: {
    marginBottom: 24,
  },
  
  modalSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  modalText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 4,
  },
  
  modalMilestone: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  modalTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  
  modalTag: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  
  modalTagText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
});
